import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Wallet, createChildWallet } from '@festpix/services';
import { colors, spacing, borderRadius, typography, shadows } from '@festpix/ui';
import AttendeeLayout from '../../components/AttendeeLayout';

interface LocationState {
  parentWallet: Wallet;
}

export default function CreateChildWalletScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const { parentWallet } = (location.state as LocationState) || {};

  const [childName, setChildName] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [createdWallet, setCreatedWallet] = useState<Wallet | null>(null);
  const [error, setError] = useState<string | null>(null);

  const isValidName = childName.trim().length >= 2;

  const handleCreate = async () => {
    if (!isValidName) {
      setError('Por favor, insira um nome válido (mínimo 2 caracteres)');
      return;
    }

    if (!parentWallet) {
      setError('Carteira pai não encontrada');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const newWallet = await createChildWallet(parentWallet.id, childName.trim());
      setCreatedWallet(newWallet);
      setSuccess(true);
    } catch (err) {
      console.error('Error creating child wallet:', err);
      setError(err instanceof Error ? err.message : 'Não foi possível criar a carteira');
    } finally {
      setLoading(false);
    }
  };

  if (success && createdWallet) {
    return (
      <AttendeeLayout>
        <div style={styles.successContainer}>
          <div style={styles.successIcon}>
            <span style={styles.successEmoji}>✓</span>
          </div>
          <h1 style={styles.successTitle}>Carteira Criada!</h1>
          <p style={styles.successMessage}>
            A carteira de <strong>{createdWallet.childName}</strong> foi criada com sucesso.
          </p>

          <div style={styles.infoCard}>
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Nome:</span>
              <span style={styles.infoValue}>{createdWallet.childName}</span>
            </div>
            <div style={styles.infoDivider} />
            <div style={styles.infoRow}>
              <span style={styles.infoLabel}>Saldo inicial:</span>
              <span style={styles.infoValue}>R$ 0,00</span>
            </div>
          </div>

          <div style={styles.tipCard}>
            <p style={styles.tipText}>
              Para adicionar saldo, volte à tela inicial e toque em "Transferir" na carteira de {createdWallet.childName}.
            </p>
          </div>

          <button
            style={styles.primaryButton}
            onClick={() => navigate('/')}
          >
            Voltar
          </button>
        </div>
      </AttendeeLayout>
    );
  }

  return (
    <AttendeeLayout showBackButton>
      <div style={styles.scrollContent}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerIcon}>
            <span style={styles.headerEmoji}>+</span>
          </div>
          <h1 style={styles.headerTitle}>Nova Carteira Filho</h1>
          <p style={styles.headerSubtitle}>
            Crie uma carteira para seu filho usar no evento
          </p>
        </div>

        {/* Name Input */}
        <div style={styles.inputSection}>
          <label style={styles.inputLabel}>Nome da criança</label>
          <input
            style={styles.input}
            value={childName}
            onChange={(e) => setChildName(e.target.value)}
            placeholder="Ex: Maria, João..."
            autoFocus
          />
          {childName.length > 0 && childName.length < 2 && (
            <p style={styles.hintText}>Mínimo 2 caracteres</p>
          )}
        </div>

        {error && <p style={styles.errorText}>{error}</p>}

        {/* Info Section */}
        <div style={styles.infoSection}>
          <p style={styles.infoSectionTitle}>Como funciona:</p>
          <div style={styles.infoBullet}>
            <span style={styles.bulletDot}>1.</span>
            <span style={styles.bulletText}>
              A carteira será criada com saldo zero
            </span>
          </div>
          <div style={styles.infoBullet}>
            <span style={styles.bulletDot}>2.</span>
            <span style={styles.bulletText}>
              Você pode transferir saldo para a carteira a qualquer momento
            </span>
          </div>
          <div style={styles.infoBullet}>
            <span style={styles.bulletDot}>3.</span>
            <span style={styles.bulletText}>
              Peça ao administrador para imprimir o QR Code para uso no evento
            </span>
          </div>
        </div>

        {/* Create Button */}
        <button
          style={{
            ...styles.primaryButton,
            ...(!isValidName ? styles.primaryButtonDisabled : {})
          }}
          onClick={handleCreate}
          disabled={!isValidName || loading}
        >
          {loading ? 'Criando...' : 'Criar Carteira'}
        </button>

        {/* Cancel Button */}
        <button
          style={styles.cancelButton}
          onClick={() => navigate('/')}
          disabled={loading}
        >
          Cancelar
        </button>
      </div>
    </AttendeeLayout>
  );
}

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    minHeight: '100vh',
    backgroundColor: colors.background,
  },
  scrollContent: {
    padding: spacing.lg,
    paddingBottom: spacing.xl,
  },

  // Header
  header: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    marginBottom: spacing.xl,
  },
  headerIcon: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: colors.primary50,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: spacing.md,
  },
  headerEmoji: {
    fontSize: 40,
    color: colors.primary,
    fontWeight: typography.fontWeight.bold,
  },
  headerTitle: {
    fontSize: typography.fontSize['2xl'],
    fontWeight: typography.fontWeight.bold,
    color: colors.text,
    marginBottom: spacing.xs,
    margin: 0,
  },
  headerSubtitle: {
    fontSize: typography.fontSize.md,
    color: colors.textSecondary,
    textAlign: 'center',
    margin: 0,
    marginTop: spacing.xs,
  },

  // Input Section
  inputSection: {
    marginBottom: spacing.lg,
  },
  inputLabel: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
    color: colors.textSecondary,
    marginBottom: spacing.sm,
    display: 'block',
  },
  input: {
    width: '100%',
    backgroundColor: colors.cardBackground,
    borderRadius: borderRadius.lg,
    border: `2px solid ${colors.border}`,
    padding: spacing.md,
    fontSize: typography.fontSize.lg,
    color: colors.text,
    boxShadow: shadows.sm,
    outline: 'none',
    boxSizing: 'border-box',
  },
  hintText: {
    fontSize: typography.fontSize.xs,
    color: colors.textSecondary,
    marginTop: spacing.xs,
    margin: 0,
  },
  errorText: {
    fontSize: typography.fontSize.sm,
    color: colors.error,
    textAlign: 'center',
    marginBottom: spacing.md,
  },

  // Info Section
  infoSection: {
    backgroundColor: colors.infoBg,
    borderRadius: borderRadius.lg,
    padding: spacing.md,
    marginBottom: spacing.xl,
  },
  infoSectionTitle: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.semibold,
    color: colors.info,
    marginBottom: spacing.md,
    margin: 0,
  },
  infoBullet: {
    display: 'flex',
    marginBottom: spacing.sm,
  },
  bulletDot: {
    fontSize: typography.fontSize.sm,
    color: colors.info,
    fontWeight: typography.fontWeight.bold,
    marginRight: spacing.sm,
    minWidth: 20,
  },
  bulletText: {
    flex: 1,
    fontSize: typography.fontSize.sm,
    color: colors.text,
    lineHeight: 1.4,
  },

  // Buttons
  primaryButton: {
    backgroundColor: colors.primary,
    padding: spacing.md,
    borderRadius: borderRadius.md,
    color: colors.textLight,
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold,
    width: '100%',
    marginBottom: spacing.md,
    boxShadow: shadows.colored,
    border: 'none',
    cursor: 'pointer',
  },
  primaryButtonDisabled: {
    backgroundColor: colors.neutral400,
    boxShadow: 'none',
    cursor: 'not-allowed',
  },
  cancelButton: {
    padding: spacing.md,
    backgroundColor: 'transparent',
    color: colors.textSecondary,
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.medium,
    width: '100%',
    border: 'none',
    cursor: 'pointer',
  },

  // Success Screen
  successContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    padding: spacing.lg,
  },
  successIcon: {
    width: 100,
    height: 100,
    borderRadius: 50,
    backgroundColor: colors.success,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: spacing.lg,
  },
  successEmoji: {
    fontSize: 50,
    color: colors.textLight,
  },
  successTitle: {
    fontSize: typography.fontSize['2xl'],
    fontWeight: typography.fontWeight.bold,
    color: colors.text,
    marginBottom: spacing.sm,
    margin: 0,
  },
  successMessage: {
    fontSize: typography.fontSize.md,
    color: colors.textSecondary,
    textAlign: 'center',
    marginBottom: spacing.xl,
    margin: 0,
    marginTop: spacing.sm,
  },
  infoCard: {
    backgroundColor: colors.cardBackground,
    borderRadius: borderRadius.lg,
    padding: spacing.lg,
    width: '100%',
    maxWidth: 360,
    marginBottom: spacing.md,
    boxShadow: shadows.md,
  },
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  infoLabel: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
  },
  infoValue: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.bold,
    color: colors.text,
  },
  infoDivider: {
    height: 1,
    backgroundColor: colors.border,
    margin: `${spacing.md}px 0`,
  },
  tipCard: {
    backgroundColor: colors.infoBg,
    borderRadius: borderRadius.md,
    padding: spacing.md,
    width: '100%',
    maxWidth: 360,
    marginBottom: spacing.xl,
  },
  tipText: {
    fontSize: typography.fontSize.sm,
    color: colors.info,
    textAlign: 'center',
    lineHeight: 1.5,
    margin: 0,
  },
};
